import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi, formatDate, formatMoney } from "../Helpers";
import { getInlineLoader } from "../Helpers";
import {
  MdBadge,
  MdCalendarToday,
  MdDirectionsBike,
  MdOutlinePayments,
  MdPerson,
  MdQrCode2,
  MdRoute,
  MdVerified,
} from "react-icons/md";
import Receipt from "./Receipt";

export default function View({ uuid }) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [data, setData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  async function init() {
    setReady(false);
    await getData();
    setReady(true);
  }

  async function getData() {
    const response = await callApi("get-sale-data", { uuid });
    if (response.status === 1) {
      setData(response.data);
    } else {
      appContext.tellError("Could not fetch data");
    }
  }

  useEffect(() => {
    init();
  }, [uuid]);

  if (!ready || !data) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  // 1. Receipt Preview View
  if (showReceipt) {
    return (
      <div className="container mt-4">
        <button className="btn btn-secondary mb-3" onClick={() => setShowReceipt(false)}>
          &larr; Back to Details
        </button>
        <div className="d-flex justify-content-center">
          <Receipt saleData={data} />
        </div>
      </div>
    );
  }

  // 2. Main Record View
  const renderDetailField = (label, value, icon = null) => (
    <div className="col-md-6 mb-4">
      <label className="form-label text-dark font-semi-bold">{label}</label>
      <h6 className="d-flex align-items-center gap-2 mb-0 text-muted">
        {icon}
        <span>{value || "Not set"}</span>
      </h6>
    </div>
  );

  return (
    <div className="container">
      <div className="detail-shell">
        <div className="detail-header d-flex flex-column">
          <div className="">
            <p className="app-page-eyebrow">Sale record</p>
            <h4 className="main-section-title mb-1">{data.fullname}</h4>
          </div>
          <div className="entity-card-chip-row">
            <span className={`status-pill ${data.saleType === "loan" ? "status-pill-warning" : "status-pill-success"}`}>
              {data.saleType || "sale"}
            </span>
            <span className="status-pill status-pill-primary">{formatMoney(data.price || 0)}</span>
          </div>
        </div>

        <div className="detail-actions-card mt-4">
          <h6 className="card-title mb-4 font-bold text-primary">Sale Overview</h6>
          <div className="row">
            {renderDetailField("Customer", data.fullname, <MdPerson size={16} />)}
            {renderDetailField("Phone", data.phone, <MdPerson size={16} />)}
            {renderDetailField("Model", data.model, <MdDirectionsBike size={16} />)}
            {renderDetailField("Type", data.saleType?.toUpperCase(), <MdOutlinePayments size={16} />)}
          </div>
        </div>

        <div className="detail-actions-card mt-4">
          <h6 className="card-title mb-4 font-bold">Tracking & Contract Details</h6>
          <div className="row">
            {renderDetailField("Registration", data.regNumber, <MdBadge size={16} />)}
            {renderDetailField("GPS Tracker", data.gpsTrackerNumber, <MdQrCode2 size={16} />)}
            {renderDetailField("Duration", data.saleType === "loan" ? data.durationMonths : "N/A", <MdRoute size={16} />)}
            {renderDetailField("Price", formatMoney(data.price || 0), <MdOutlinePayments size={16} />)}
          </div>
        </div>

        <div className="detail-actions-card mt-4 bg-light">
          <h6 className="card-title mb-4 text-primary font-bold">System Details</h6>
          <div className="row">
            <div className="col-md-6 mb-4">
              <label className="form-label">AUTHORIZED BY</label>
              <h6 className="d-flex align-items-center gap-2 mb-0 text-primary">
                <MdVerified size={18} />
                <span className="fw-bold">{data.sellerName || "System"}</span>
              </h6>
            </div>

            {renderDetailField("ISSUED ON", data.dateCreated ? formatDate(data.dateCreated) : "", <MdCalendarToday size={16} />)}
            
          </div>
        </div>

        <div className="detail-actions-card mt-4">
          <h6 className="card-title font-bold text-primary mb-3">Actions</h6>
          <div className="detail-actions-row">
            {data.debtId && (
              <button
                className="btn btn-dark btn-rounded app-cta-btn"
                onClick={() => appContext.navTo({ item: "view", subItem: "create-payment", extraItem: data.debtId })}
              >
                <MdOutlinePayments size={18} /> Record payment
              </button>
            )}
            <button 
              className="btn btn-outline-primary btn-rounded app-cta-btn" 
              onClick={() => setShowReceipt(true)}
            >
              <MdQrCode2 size={18} /> View Receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}